import {
  filledPortalTableRows,
  type PortalDraftValue,
  type PortalRequestResponse,
  type PortalSessionResponse,
} from "@ledgerbox/domain"

/* A text answer, an amount or a table is kept while the client types, so a
   closed tab, a collapsed row or another device picks up where they
   stopped. Everything here is plain logic; the checklist wires it to the
   field. */

type Item = PortalRequestResponse["items"][number]
export type ItemDraft = NonNullable<Item["draft"]>

/** How long the field waits after the last key before it saves. */
export const draftSaveDelayMs = 600

/**
 * The time stamped on a save. It is the device's clock, moved past the
 * newest draft or answer this device has seen, so a phone whose clock runs
 * slow still writes after the laptop it follows.
 */
export const draftSavedAt = (
  nowMs: number,
  known: readonly (string | null | undefined)[]
): string => {
  const latest = known.reduce((newest, value) => {
    const at = value ? Date.parse(value) : Number.NaN
    return Number.isFinite(at) && at > newest ? at : newest
  }, Number.NEGATIVE_INFINITY)
  return new Date(Math.max(nowMs, latest + 1)).toISOString()
}

/** The session with one item's draft replaced, when the new one is newer. */
export const withItemDraft = (
  session: PortalSessionResponse,
  itemId: string,
  draft: ItemDraft
): PortalSessionResponse => ({
  ...session,
  requests: session.requests.map((request) =>
    request.items.some((item) => item.itemId === itemId)
      ? {
          ...request,
          items: request.items.map((item) =>
            item.itemId === itemId &&
            (!item.draft || item.draft.savedAt < draft.savedAt)
              ? { ...item, draft }
              : item
          ),
        }
      : request
  ),
})

/** What the field says under itself. */
export type DraftStatus<V extends PortalDraftValue = string> =
  | { kind: "idle" }
  | { kind: "saved"; value: V }
  | { kind: "failed"; message: string }

/** A draft as the answer would read it: the text trimmed, or the rows that
 * are not blank. Two drafts with one key send the same answer. */
export const draftAnswerKey = (value: PortalDraftValue): string =>
  typeof value === "string"
    ? value.trim()
    : JSON.stringify(filledPortalTableRows(value))

const sameDraft = (left: PortalDraftValue, right: PortalDraftValue) =>
  typeof left === "string"
    ? left === right
    : JSON.stringify(left) === JSON.stringify(right)

/**
 * The line under the field, or null. "Saved" only while the field holds
 * exactly the saved draft and that draft is not already the answer; a
 * failure says what went wrong until the next key tries again.
 */
export const draftNote = <V extends PortalDraftValue>(
  status: DraftStatus<V>,
  value: V,
  answered: V
): { message: string; failed: boolean } | null => {
  if (status.kind === "failed") return { message: status.message, failed: true }
  if (
    status.kind === "saved" &&
    sameDraft(status.value, value) &&
    draftAnswerKey(value) !== draftAnswerKey(answered)
  )
    return { message: "Saved", failed: false }
  return null
}

type Timers = {
  set(run: () => void, ms: number): unknown
  clear(handle: unknown): void
}

const browserTimers: Timers = {
  set: (run, ms) => setTimeout(run, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}

/**
 * Saves the newest draft a moment after the client stops typing. One save
 * is on the wire at a time and only the newest waiting draft goes next, so
 * the server never sees an older draft after a newer one from this tab.
 */
export function createDraftSaver<V extends PortalDraftValue = string>(options: {
  delayMs?: number
  save(value: V): Promise<{ saved: boolean; draft: ItemDraft | null }>
  onDraft(draft: ItemDraft): void
  onStatus(status: DraftStatus<V>): void
  errorMessage(error: unknown): string
  timers?: Timers
}) {
  const timers = options.timers ?? browserTimers
  let waiting: V | null = null
  let timer: unknown = null
  let running: Promise<void> | null = null

  const stopTimer = () => {
    if (timer !== null) timers.clear(timer)
    timer = null
  }

  const drain = async () => {
    while (waiting !== null) {
      const value = waiting
      waiting = null
      try {
        const result = await options.save(value)
        if (result.draft) options.onDraft(result.draft)
        // A newer text is already waiting; only the last save may say Saved.
        if (waiting === null)
          options.onStatus(
            result.saved ? { kind: "saved", value } : { kind: "idle" }
          )
      } catch (error) {
        if (waiting === null)
          options.onStatus({
            kind: "failed",
            message: options.errorMessage(error),
          })
      }
    }
  }

  const start = () => {
    if (running) return
    running = drain().finally(() => {
      running = null
    })
  }

  return {
    /** The field changed. */
    schedule(value: V) {
      waiting = value
      stopTimer()
      options.onStatus({ kind: "idle" })
      timer = timers.set(() => {
        timer = null
        start()
      }, options.delayMs ?? draftSaveDelayMs)
    },
    /** Saves the waiting text now, e.g. when the row closes. */
    flush(): Promise<void> {
      if (timer !== null) {
        stopTimer()
        start()
      }
      return running ?? Promise.resolve()
    },
    /** Drops the waiting text and lets a save on the wire finish, so the
        answer that follows is the last write. */
    settle(): Promise<void> {
      stopTimer()
      waiting = null
      return running ?? Promise.resolve()
    },
  }
}
